import React from 'react';
import { motion } from "framer-motion";
import { Shield } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-slate-400">Last Updated: March 2026</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Privacy <span className="gradient-text">Policy</span>
          </h1>
          <p className="text-lg text-slate-400">
            How we collect, use, and protect your data.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose prose-invert prose-slate max-w-none space-y-8"
        >
          <section className="bg-slate-900/50 border border-slate-800/50 rounded-2xl p-8 md:p-12 text-slate-300">
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p className="mb-6 leading-relaxed">
              At Luxkgate, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our payment gateway infrastructure.
            </p>
            <p className="mb-6 leading-relaxed">
              Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or utilize our services.
            </p>

            <h2 className="text-2xl font-bold text-white mb-4 mt-12">2. Information We Collect</h2>
            <p className="mb-6 leading-relaxed">
              We may collect information about you in a variety of ways. The information we may collect via the Site includes:
            </p>
            <ul className="list-disc pl-6 space-y-3 mb-6 opacity-90">
              <li><strong>Personal Data:</strong> Personally identifiable information, such as your name, shipping address, email address, and telephone number, and demographic information.</li>
              <li><strong>Financial Data:</strong> Financial information securely tokenized and transmitted to process payments (e.g., credit card numbers, card brand, expiration date). Note: As a PCI-DSS compliant entity, we do not store raw primary account numbers directly unless vaulted within highly encrypted, audited infrastructure.</li>
              <li><strong>Derivative Data:</strong> Information our servers automatically collect when you access the Site, such as your IP address, browser type, and operating system.</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-4 mt-12">3. Use of Your Information</h2>
            <p className="mb-4 leading-relaxed">
              Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Site to:
            </p>
            <ul className="list-disc pl-6 space-y-3 mb-6 opacity-90">
              <li>Create and manage your account securely.</li>
              <li>Process transactions and send related data, including confirmations and receipts.</li>
              <li>Resolve disputes and troubleshoot problems.</li>
              <li>Respond to product and customer service requests.</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-4 mt-12">4. Disclosure of Your Information</h2>
            <p className="mb-6 leading-relaxed">
              We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
            </p>
            <ul className="list-disc pl-6 space-y-3 mb-6 opacity-90">
              <li><strong>By Law or to Protect Rights:</strong> If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others.</li>
              <li><strong>Third-Party Service Providers:</strong> We may share your information with third parties that perform services for us, such as payment processing, data analysis, email delivery, hosting services, and customer service.</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-4 mt-12">5. Security of Your Information</h2>
            <p className="mb-6 leading-relaxed">
              We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
            </p>

            <h2 className="text-2xl font-bold text-white mb-4 mt-12">6. Contact Us</h2>
            <p className="leading-relaxed">
              If you have questions or comments about this Privacy Policy, please contact us at: <br />
              <a href="mailto:privacy@luxkgate.com" className="text-blue-400 hover:text-blue-300 transition-colors">privacy@luxkgate.com</a>
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;